import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, CreditCard, Loader2, Receipt, Scale, Wallet } from "lucide-react";
import { DateRangePicker, type DateRangePreset, type DateRangeValue } from "@wa/ui";
import { useI18n } from "../../../i18n/I18nProvider";
import { paymentMethodLabel } from "../../../lib/paymentLabel";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { formatCents } from "../../../lib/sales";
import { deletePaymentById, getPaymentMethods, getPayments, type PaymentRow } from "../../../lib/payments";
import {
  Btn,
  Card,
  EmptyState,
  MenuField,
  Modal,
  SearchField,
  Toolbar,
  ToolbarGap,
  fmtNum,
  listCard,
  useToast,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { PAYMENT_COLS, PaymentListRow } from "./PaymentListRow";

/** The design's page size. The old list asked for 50 rows and drew no pager at
 *  all, so payment 51 was simply invisible. */
const PAGE_SIZE = 25;

const ALL = "all";

/** Local YYYY-MM-DD (not toISOString - that shifts across the UTC boundary). */
function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * The date-range picker is the one control on this page with no CRM equivalent:
 * a two-month calendar with a preset rail is a lot of component to rewrite, and
 * `@wa/ui` already has a good one. It arrives wearing the shadcn outline button,
 * so it is dressed here in the toolbar's own pill. The utility classes are not
 * decoration - each one cancels a class the shared Button hard-codes (h-8, px-3,
 * text-xs, rounded-md, border, the accent hover), which would otherwise beat the
 * `crm-fld` rules because Tailwind's utilities layer sits above components.
 */
const RANGE_FIELD_CLASS =
  "crm-fld crm-fld-sel h-11 gap-2.5 rounded-full border-0 px-[18px] text-[13px] font-semibold hover:bg-card hover:text-inherit";

/** One figure from the payments strip, in the shape `SalesShell` uses. */
function Stat({ icon, label, value, sub }: { icon: ReactNode; label: string; value: string; sub?: ReactNode }) {
  return (
    <div className="crm-q">
      <span className="crm-q-tile">{icon}</span>
      <span className="min-w-0">
        <span className="crm-q-lab block">{label}</span>
        <span className="crm-q-val block">{value}</span>
        {sub ? <span className="crm-prow-s block">{sub}</span> : null}
      </span>
    </div>
  );
}

/**
 * The cross-invoice payments ledger.
 *
 * It renders inside `SalesShell`, so the page heading is already on screen and
 * this component starts at the figures. The strip above the list is the one
 * number the old screen showed in the corner, split into the three that can
 * honestly be derived from what the endpoint returns: it counts and sums the
 * whole filtered set, not the visible page.
 */
export function PaymentsList({ user }: { user: StaffUser }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [sumCents, setSumCents] = useState(0);
  const [total, setTotal] = useState(0);
  const [methods, setMethods] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [method, setMethod] = useState(ALL);
  const [range, setRange] = useState<DateRangeValue | null>(null);
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<PaymentRow | null>(null);
  const [busy, setBusy] = useState(false);
  const canManage = userCanPlatform(user, "manage:crm.sales");

  const filtered = q !== "" || method !== ALL || range !== null;

  // Quick ranges are evaluated on click, so "today"-relative presets stay fresh.
  const presets = useMemo<DateRangePreset[]>(() => {
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const back = (days: number) => {
      const d = new Date();
      d.setDate(d.getDate() - days);
      return startOfDay(d);
    };
    return [
      { label: t("sales.payments.range.7d"), getRange: () => ({ from: back(6), to: startOfDay(new Date()) }) },
      { label: t("sales.payments.range.30d"), getRange: () => ({ from: back(29), to: startOfDay(new Date()) }) },
      {
        label: t("sales.payments.range.month"),
        getRange: () => {
          const n = new Date();
          return { from: new Date(n.getFullYear(), n.getMonth(), 1), to: startOfDay(n) };
        },
      },
      {
        label: t("sales.payments.range.year"),
        getRange: () => {
          const n = new Date();
          return { from: new Date(n.getFullYear(), 0, 1), to: startOfDay(n) };
        },
      },
    ];
  }, [t]);

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  useEffect(() => {
    getPaymentMethods()
      .then((r) => setMethods(r.rows))
      .catch(() => {});
  }, []);

  // Any change to what is being asked for starts at the first page. Staying on
  // page 4 of a result set that now has one page shows an empty card and looks
  // like the filter found nothing.
  useEffect(() => {
    setPage(1);
  }, [q, method, range]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPayments({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        method: method === ALL ? undefined : method,
        from: range ? isoDay(range.from) : undefined,
        to: range ? isoDay(range.to) : undefined,
      });
      setRows(res.rows);
      setSumCents(res.sumCents);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [q, method, range, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmDelete() {
    const row = pending;
    if (!row || busy) return;
    setBusy(true);
    try {
      await deletePaymentById(row.id);
      setPending(null);
      await load();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  function clearFilters() {
    setQInput("");
    setQ("");
    setMethod(ALL);
    setRange(null);
  }

  return (
    // One open menu at a time across every row and every filter.
    <PopHost>
      {/* The figures the old screen crammed into a corner. `sumCents` and
          `total` come from the endpoint and cover the whole filtered set, so
          the average below is a real average and not the visible page's. */}
      <Card className="crm-quick" style={{ "--crm-quick-cols": 3 } as CSSProperties}>
        <Stat
          icon={<Receipt className="size-4" />}
          label={t("sales.payments.stat.count")}
          value={fmtNum(total)}
          sub={filtered ? t("sales.payments.stat.filtered") : undefined}
        />
        <Stat icon={<Wallet className="size-4" />} label={t("sales.payments.stat.sum")} value={formatCents(sumCents)} />
        <Stat
          icon={<Scale className="size-4" />}
          label={t("sales.payments.stat.avg")}
          value={formatCents(total ? Math.round(sumCents / total) : 0)}
        />
      </Card>

      <Toolbar>
        <SearchField value={qInput} onChange={setQInput} placeholder={t("sales.payments.search")} />
        <MenuField
          value={method}
          onChange={setMethod}
          label={t("sales.payments.method")}
          options={[
            { value: ALL, label: t("sales.payments.methodAny") },
            // The VALUE stays the stored string, because that is what the
            // route matches on, exactly and case-sensitively. Only the LABEL is
            // translated, and only for the two built-in tokens: an operator's
            // own name and an imported one both read as themselves, which is
            // what makes an unfamiliar entry recognisable rather than mystifying.
            ...methods.map((m) => {
              // The same resolver the rows underneath use. Two answers to this
              // question is how the filter came to offer "Bank transfer" over a
              // table full of `pangaülekanne`.
              return { value: m, label: paymentMethodLabel(m, t, m) };
            }),
          ]}
        />
        <DateRangePicker
          value={range}
          onChange={setRange}
          presets={presets}
          locale={locale}
          placeholder={t("sales.payments.rangeAny")}
          className={RANGE_FIELD_CLASS}
        />
        <ToolbarGap />
        {filtered ? <Btn small onClick={clearFilters}>{t("common.clear")}</Btn> : null}
      </Toolbar>

      <div className="crm-card crm-list-stack" style={listCard(PAYMENT_COLS)}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={CreditCard}
            title={t("sales.payments.empty")}
            body={filtered ? t("sales.payments.emptyFiltered") : t("sales.payments.emptyBody")}
            action={filtered ? <Btn onClick={clearFilters}>{t("common.clear")}</Btn> : undefined}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: PAYMENT_COLS, paddingTop: 22 }}>
              <span>{t("sales.col.date")}</span>
              <span>{t("sales.payments.invoice")}</span>
              <span>{t("sales.col.client")}</span>
              <span>{t("sales.payments.method")}</span>
              <span>{t("sales.payments.reference")}</span>
              <span className="text-right">{t("sales.payments.amount")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((p) => (
                <PaymentListRow
                  key={p.id}
                  payment={p}
                  invoiceLabel={t("sales.payments.invoice")}
                  canManage={canManage}
                  onDelete={setPending}
                />
              ))}
            </div>

            <div className="crm-tfoot">
              {t("sales.showing", {
                from: (page - 1) * PAGE_SIZE + 1,
                to: (page - 1) * PAGE_SIZE + rows.length,
                total,
              })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      {pending ? (
        <Modal
          title={t("sales.payments.deleteTitle")}
          label={t("sales.payments.deleteTitle")}
          onClose={() => setPending(null)}
          footer={
            <>
              <Btn onClick={() => setPending(null)} disabled={busy}>
                {t("common.cancel")}
              </Btn>
              <Btn tone="danger" onClick={() => void confirmDelete()} disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                {t("common.delete")}
              </Btn>
            </>
          }
        >
          <p className="crm-sub">{t("sales.payments.deleteConfirm")}</p>
        </Modal>
      ) : null}
    </PopHost>
  );
}
